import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Film, Play, Clock } from 'lucide-react';
import { fetchMyVideos, friendlyError } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';
import { AccessBadge } from '../AccessBadge';

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function MyVideosTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchMyVideos();
        if (alive) setItems(data.items || []);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={Film}
        label="Mis vídeos"
        hint="Contenidos a los que tienes acceso."
      />
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-44 w-full rounded-xl" />
        ))}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Film}
          title="Aún no tienes vídeos disponibles"
          description="Aquí aparecerán los contenidos que compres o que estén incluidos en tu suscripción."
          ctaLabel="Ver partidos disponibles"
          ctaTo="/eventos"
          testId={DASHBOARD.videosEmpty}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {items.map((v) => (
            <div
              key={v.id}
              data-testid={DASHBOARD.videoRow(v.id)}
              className="rounded-xl overflow-hidden border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 transition-colors flex flex-col"
            >
              <div className="aspect-video bg-[var(--ns-bg-2)] relative">
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt={v.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full inline-flex items-center justify-center text-[var(--ns-gold-700)]">
                    <Film className="h-8 w-8" />
                  </div>
                )}
                {v.duration_seconds ? (
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-[10px] inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {fmtDuration(v.duration_seconds)}
                  </span>
                ) : null}
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <p className="text-sm font-semibold text-[var(--ns-text)] line-clamp-2">{v.title}</p>
                {v.event?.title ? (
                  <p className="text-xs text-[var(--ns-text-muted)] truncate">{v.event.title}</p>
                ) : null}
                <div className="flex items-center gap-2 flex-wrap mt-auto">
                  <AccessBadge via={v.access_via} className="text-[10px]" />
                  {v.event?.slug ? (
                    <Button asChild size="sm" className="h-7 px-2 rounded-lg bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/25 border-0">
                      <Link to={`/eventos/${v.event.slug}/watch?v=${v.id}`} className="inline-flex items-center gap-1 text-xs">
                        <Play className="h-3 w-3" />
                        Reproducir
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
